import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { FileUploader } from "./FileUploader";
import type { Document } from "@/services/api/documents/types";
import type { IngestAPIResponse } from "@/services/api/data-hub/types";

interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  uploadFunction: (file: File) => Promise<Document | IngestAPIResponse>;
  acceptedMimeTypes?: string[];
  maxSize?: number;
  onUploadComplete?: (result: Document | IngestAPIResponse) => void;
  onUploadError?: (error: Error) => void;
  onFileSelected?: (file: File) => void;
  onFilesSelected?: (files: File[]) => void;
  multiple?: boolean;
}

export const UploadDialog: React.FC<UploadDialogProps> = ({
  isOpen,
  onClose,
  title = "Upload Document",
  description = "Select a file to upload.",
  uploadFunction,
  acceptedMimeTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
    "image/jpeg",
    "image/png",
    "image/gif",
  ],
  maxSize = 20 * 1024 * 1024, // 20MB
  onUploadComplete,
  onUploadError,
  onFileSelected,
  onFilesSelected,
  multiple = false,
}) => {
  const [hasError, setHasError] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<Document | null>(
    null
  );

  const handleUploadComplete = (result: Document) => {
    // Store the result but don't render it directly
    setUploadedDocument(result);

    if (onUploadComplete) {
      onUploadComplete(result);
    }
    // Automatically close the dialog on successful upload
    onClose();
  };

  const handleUploadError = (error: Error) => {
    setHasError(true);
    if (onUploadError) {
      onUploadError(error);
    }
    // We don't close the dialog on error so the user can try again
  };

  // Custom upload complete handler that only calls onUploadComplete for immediate uploads
  const customUploadComplete = (result: Document | IngestAPIResponse) => {
    // Only call onUploadComplete if we're not using deferred upload
    if (!onFileSelected && !onFilesSelected && onUploadComplete) {
      onUploadComplete(result);
    }
    onClose();
  };

  const handleFilesSelected = (files: File[]) => {
    if (onFilesSelected) {
      onFilesSelected(files);
    } else if (onFileSelected) {
      for (const file of files) {
        onFileSelected(file);
      }
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="xl">
      <DialogTitle>{description}</DialogTitle>

      <DialogBody>
        <FileUploader
          uploadFunction={uploadFunction}
          acceptedMimeTypes={acceptedMimeTypes}
          maxSize={maxSize}
          multiple={multiple}
          onUploadComplete={customUploadComplete}
          onUploadError={handleUploadError}
          onFileSelected={
            onFileSelected && !onFilesSelected
              ? (file: File) => {
                  onFileSelected(file);
                  onClose();
                }
              : undefined
          }
          onFilesSelected={
            onFilesSelected || onFileSelected ? handleFilesSelected : undefined
          }
        />
      </DialogBody>

      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:bg-slate-800 dark:text-gray-300 dark:border-slate-700 dark:hover:bg-slate-700 dark:focus:ring-indigo-400 dark:focus:ring-offset-slate-900"
        >
          Cancel
        </button>
      </DialogActions>
    </Dialog>
  );
};
